import type { AiDialogDom } from './ai-dialog';
import type { AutoplayDom } from './autoplay';
import type { DeleterDom } from './deleter';
import type { ExportDom } from './export-dialog';
import type { PaletteDom } from './palette';
import type { SingleDom } from './single';
import type { ViewerDom } from './viewer';

export interface AppDom {
  readonly app: HTMLElement;
  readonly fonts: HTMLLinkElement;
  readonly dropZone: HTMLElement;
  readonly dropBusy: HTMLElement;
  readonly viewer: HTMLElement;
  readonly single: HTMLElement;
  readonly openFolder: HTMLButtonElement;
  readonly dropOpen: HTMLButtonElement;
  readonly reopen: HTMLButtonElement;
  readonly reopenName: HTMLElement;
  readonly folderInput: HTMLInputElement;
  readonly fileInput: HTMLInputElement;
  readonly demo: HTMLButtonElement;
  readonly search: HTMLInputElement;
  readonly sort: HTMLSelectElement;
  readonly chips: HTMLElement;
  readonly list: HTMLOListElement;
  readonly listEmpty: HTMLElement;
  readonly progress: HTMLElement;
  readonly progressFill: HTMLElement;
  readonly progressText: HTMLElement;
  readonly help: HTMLButtonElement;
  readonly helpDialog: HTMLDialogElement;
  readonly shortcuts: HTMLElement;
  readonly paletteButton: HTMLButtonElement;
  readonly palette: PaletteDom;
  readonly exportButton: HTMLButtonElement;
  readonly exportDialog: ExportDom;
  readonly aiDialog: AiDialogDom;
  readonly deleteButton: HTMLButtonElement;
  readonly deleter: DeleterDom;
  readonly autoplay: AutoplayDom;
  readonly stage: HTMLElement;
  readonly toast: HTMLElement;
  readonly viewerDom: ViewerDom;
  readonly singleDom: SingleDom;
}

export function byId<T extends Element>(id: string, type: { new (): T; prototype: T }): T {
  const element = document.getElementById(id);
  if (!(element instanceof type)) throw new Error(`#${id} is missing from the page`);
  return element;
}

const html = (id: string): HTMLElement => byId(id, HTMLElement);
const button = (id: string): HTMLButtonElement => byId(id, HTMLButtonElement);

function readViewerDom(): ViewerDom {
  return {
    badge: html('tvEventBadge'),
    title: html('tvEventTitle'),
    meta: html('tvEventMeta'),
    mapLink: byId('tvMapLink', HTMLAnchorElement),
    ai: button('tvAi'),
    grid: html('tvGrid'),
    hud: {
      root: html('tvHud'),
      speed: html('tvHudSpeed'),
      autopilot: html('tvHudAutopilot'),
      brake: html('tvHudBrake'),
      pedal: html('tvHudPedal'),
      wheel: byId('tvHudWheel', SVGSVGElement),
      steering: html('tvHudSteering'),
      blinkLeft: html('tvHudBlinkLeft'),
      blinkRight: html('tvHudBlinkRight'),
      gDot: byId('tvHudGDot', SVGCircleElement),
      g: html('tvHudG'),
    },
    scrubber: byId('tvScrubber', HTMLCanvasElement),
    scrubberTip: html('tvScrubberTip'),
    play: button('tvPlay'),
    previousMarker: button('tvPrevMarker'),
    nextMarker: button('tvNextMarker'),
    clock: html('tvClock'),
    clockGap: html('tvClockGap'),
    rates: html('tvRates'),
    gridButton: button('tvGridButton'),
  };
}

function readExportDom(): ExportDom {
  return {
    dialog: byId('tvExportDialog', HTMLDialogElement),
    modes: html('tvExportModes'),
    from: byId('tvExportFrom', HTMLInputElement),
    to: byId('tvExportTo', HTMLInputElement),
    aroundTrigger: button('tvExportAroundTrigger'),
    whole: button('tvExportWhole'),
    reelList: byId('tvReelList', HTMLOListElement),
    mergeInput: byId('tvMergeInput', HTMLInputElement),
    mergeList: byId('tvMergeList', HTMLOListElement),
    hud: byId('tvExportHud', HTMLInputElement),
    status: html('tvExportStatus'),
    fill: html('tvExportFill'),
    text: html('tvExportText'),
    problem: html('tvExportProblem'),
    download: byId('tvExportDownload', HTMLAnchorElement),
    cancel: button('tvExportCancel'),
    start: button('tvExportStart'),
  };
}

function readAiDom(): AiDialogDom {
  return {
    dialog: byId('tvAiDialog', HTMLDialogElement),
    notes: html('tvAiNotes'),
    verdict: html('tvAiVerdict'),
    reason: html('tvAiReason'),
    summary: html('tvAiSummary'),
    moments: byId('tvAiMoments', HTMLOListElement),
    about: html('tvAiAbout'),
    outdated: html('tvAiOutdated'),
    empty: html('tvAiEmpty'),
    target: html('tvAiTarget'),
    details: byId('tvAiDetails', HTMLDetailsElement),
    instructions: html('tvAiInstructions'),
    copyEvent: button('tvAiCopyEvent'),
    copyAll: button('tvAiCopyAll'),
  };
}

export function readDom(): AppDom {
  return {
    app: html('tvApp'),
    fonts: byId('tvFonts', HTMLLinkElement),
    dropZone: html('tvDropZone'),
    dropBusy: html('tvDropBusy'),
    viewer: html('tvViewer'),
    single: html('tvSingle'),
    openFolder: button('tvOpenFolder'),
    dropOpen: button('tvDropOpen'),
    reopen: button('tvReopen'),
    reopenName: html('tvReopenName'),
    folderInput: byId('tvFolderInput', HTMLInputElement),
    fileInput: byId('tvFileInput', HTMLInputElement),
    demo: button('tvDemo'),
    search: byId('tvSearch', HTMLInputElement),
    sort: byId('tvSort', HTMLSelectElement),
    chips: html('tvChips'),
    list: byId('tvList', HTMLOListElement),
    listEmpty: html('tvListEmpty'),
    progress: html('tvProgress'),
    progressFill: html('tvProgressFill'),
    progressText: html('tvProgressText'),
    help: button('tvHelp'),
    helpDialog: byId('tvHelpDialog', HTMLDialogElement),
    shortcuts: html('tvShortcuts'),
    paletteButton: button('tvPaletteButton'),
    palette: { dialog: byId('tvPalette', HTMLDialogElement), input: byId('tvPaletteInput', HTMLInputElement), list: byId('tvPaletteList', HTMLUListElement) },
    exportButton: button('tvExport'),
    exportDialog: readExportDom(),
    aiDialog: readAiDom(),
    deleteButton: button('tvDelete'),
    deleter: {
      dialog: byId('tvDeleteDialog', HTMLDialogElement),
      title: html('tvDeleteTitle'),
      text: html('tvDeleteText'),
      warning: html('tvDeleteWarning'),
      confirm: button('tvDeleteConfirm'),
    },
    autoplay: {
      toggle: button('tvAutoplay'),
      card: html('tvNext'),
      title: html('tvNextTitle'),
      bar: html('tvNextBar'),
      playNow: button('tvNextNow'),
      cancel: button('tvNextCancel'),
    },
    stage: html('tvStage'),
    toast: html('tvToast'),
    viewerDom: readViewerDom(),
    singleDom: {
      title: html('tvSingleTitle'),
      meta: html('tvSingleMeta'),
      video: byId('tvSingleVideo', HTMLVideoElement),
      chapters: byId('tvChapters', HTMLOListElement),
      videos: byId('tvVideos', HTMLOListElement),
      merge: button('tvSingleMerge'),
    },
  };
}
